/**
 * Deck store — leaf module (Perf 3.1.b Step 5).
 */
import { create } from 'zustand';
import { Platform } from 'react-native';
import { persist, createJSONStorage } from 'zustand/middleware';
import { decksApi } from '@/lib/api/decks';
import { getStorage } from './storage';
import { useToastStore } from './toast';


// ==================== Deck Store ====================
// Multi-deck workspace ("TweetDeck for AI"). Each user can have up to
// MAX_DECKS_PER_USER decks; each deck has up to MAX_DECK_COLUMNS columns
// referencing a Conversation, Model, Agent, or Tool.
//
// Source of truth: `state.decks` (array, server-synced). The current
// `state.activeDeckSlug` selects which deck is showing. `state.columns`,
// `state.columnOrder`, and `state.focusedColumnId` are MIRRORED from the
// active deck so the existing column/header components keep working
// without any changes.

import { DEFAULT_DECK_COLUMN_WIDTH, MIN_DECK_COLUMN_WIDTH, MAX_DECK_COLUMN_WIDTH, MAX_DECK_COLUMNS, MAX_DECKS_PER_USER } from '@/constants/config';

// ---- helpers (module-private) -----------------------------------------
// Stable id for a brand-new column.
const _newColId = () => `col-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

// Pull a deck's [columns, columnOrder] mirror onto the top-level state.
// We keep BOTH the new `decks[]` source of truth AND the legacy mirrors
// so existing callers (`state.columns`, `state.columnOrder`,
// `state.focusedColumnId`) keep working unchanged.
const _mirror = (deck, focusedColumnId = null) => {
  if (!deck) return { columns: [], columnOrder: [], focusedColumnId: null };
  const cols = _normalizeClientDeckColumns(Array.isArray(deck.columns) ? deck.columns : []);
  // Drop focus if the focused column no longer exists.
  const stillFocused = focusedColumnId && cols.some(c => c.id === focusedColumnId);
  return {
    columns: cols,
    columnOrder: cols.map(c => c.id),
    focusedColumnId: stillFocused ? focusedColumnId : null,
  };
};

const _findDeck = (state, slug) => state.decks.find(d => d.slug === slug);
const _activeDeck = (state) => _findDeck(state, state.activeDeckSlug);

// Server deck columns persist `ref.id` for conversations but not `conversationId`.
// Mirror that onto the client shape `DeckColumn` expects.
const _normalizeClientDeckColumns = (cols) => {
  if (!Array.isArray(cols)) return [];
  return cols.map((c) => {
    if (c.type === 'conversation' && c.ref?.kind === 'Conversation' && c.ref?.id && !c.conversationId) {
      return { ...c, conversationId: String(c.ref.id) };
    }
    return c;
  });
};

// Serialize writes per deck. A slow, older request must finish before a
// newer layout can be saved. Session changes invalidate queued callbacks.
const _saveTimers = {};
const _writeChains = new Map();
const _dirtyDecks = new Map();
const UNSAVED_LAYOUT_MESSAGE = 'Your layout has not been saved. Check your connection and retry.';
let _session = 0;
const _enqueueWrite = (slug, task) => {
  const session = _session;
  const previous = _writeChains.get(slug) || Promise.resolve();
  const next = previous.catch(() => {}).then(() => {
    if (session === _session) return task();
  });
  _writeChains.set(slug, next);
  const cleanup = () => { if (_writeChains.get(slug) === next) _writeChains.delete(slug); };
  next.then(cleanup, cleanup);
  return next;
};
const _saveDeck = (slug, getDeck) => {
  const session = _session;
  return _enqueueWrite(slug, async () => {
    const version = _dirtyDecks.get(slug);
    const deck = getDeck();
    if (!version || !deck) return;
    try {
      // Metadata is saved separately; a layout save must not revert a rename.
      await decksApi.update(slug, { columns: deck.columns });
      if (session !== _session) return;
      if (_dirtyDecks.get(slug) === version) _dirtyDecks.delete(slug);
      useDeckStore.setState({ pendingColumnSlugs: [..._dirtyDecks.keys()], ...(!_dirtyDecks.size ? { syncError: null } : {}) });
    } catch {
      if (session === _session) useDeckStore.setState({ syncError: UNSAVED_LAYOUT_MESSAGE });
    }
  });
};
const _scheduleDeckSave = (slug, getDeck) => {
  if (Platform.OS === 'web' && typeof window === 'undefined') return;
  _dirtyDecks.set(slug, (_dirtyDecks.get(slug) || 0) + 1);
  useDeckStore.setState({ pendingColumnSlugs: [..._dirtyDecks.keys()] });
  if (_saveTimers[slug]) clearTimeout(_saveTimers[slug]);
  _saveTimers[slug] = setTimeout(() => {
    delete _saveTimers[slug];
    void _saveDeck(slug, getDeck);
  }, 500);
};

const useDeckStore = create(
  persist(
    (set, get) => ({
      // ── Multi-deck source of truth ────────────────────────────────────
      decks: [],                 // Array<DeckDocument>
      activeDeckSlug: null,      // Slug of the currently visible deck
      isLoadingDecks: false,
      hasLoadedOnce: false,
      syncError: null,
      pendingColumnSlugs: [],
      limits: { maxDecks: MAX_DECKS_PER_USER, maxColumns: MAX_DECK_COLUMNS },

      // ── Active-deck mirrors (back-compat with existing callers) ──────
      columns: [],
      columnOrder: [],
      focusedColumnId: null,

      actions: {
        // ── Multi-deck management ──────────────────────────────────────

        // Pull all decks for the current user. Cheap to call repeatedly;
        // updates the active deck mirror after a fetch.
        loadDecks: async () => {
          if (get().isLoadingDecks) return;
          const session = _session;
          const beforeFetch = get().decks;
          set({ isLoadingDecks: true });
          try {
            const data = await decksApi.list();
            if (session !== _session) return;
            const fetched = Array.isArray(data?.decks) ? data.decks : [];
            // Keep edits made during the fetch and layouts still awaiting a
            // successful save. A refresh is never permission to discard them.
            const decks = get().decks !== beforeFetch || _writeChains.has('tab-order') ? get().decks : fetched.map(d =>
              _dirtyDecks.has(d.slug) || _writeChains.has(d.slug) ? (_findDeck(get(), d.slug) || d) : d);
            const limits = data?.limits || { maxDecks: MAX_DECKS_PER_USER, maxColumns: MAX_DECK_COLUMNS };

            // Keep the user's currently-active deck if it still exists,
            // otherwise fall back to the first deck (or null).
            const currentSlug = get().activeDeckSlug;
            const nextActive = decks.find(d => d.slug === currentSlug) ? currentSlug
              : (decks[0]?.slug || null);
            const activeDeck = decks.find(d => d.slug === nextActive);

            set({
              decks,
              activeDeckSlug: nextActive,
              isLoadingDecks: false,
              hasLoadedOnce: true,
              limits,
              ..._mirror(activeDeck, get().focusedColumnId),
            });
          } catch (err) {
            if (session !== _session) return;
            set({ isLoadingDecks: false, hasLoadedOnce: true, syncError: 'Could not load your decks. Check your connection and retry.' });
            if (typeof console !== 'undefined') console.warn('[decks] list failed', err?.message || err);
          }
        },

        retrySync: async () => {
          set({ syncError: null });
          await Promise.all([..._dirtyDecks.keys()].map(slug => {
            clearTimeout(_saveTimers[slug]);
            delete _saveTimers[slug];
            return _saveDeck(slug, () => _findDeck(get(), slug));
          }));
          if (!get().syncError) await get().actions.loadDecks();
        },

        // Set which deck is visible. Mirrors its columns/order onto the
        // legacy top-level state so existing components rerender.
        setActiveDeckSlug: (slug) => {
          const state = get();
          if (slug === state.activeDeckSlug) return;
          const deck = _findDeck(state, slug);
          if (!deck) return;
          set({
            activeDeckSlug: slug,
            ..._mirror(deck, null),
          });
        },

        // Create a new deck (blank, or seeded from a template).
        // Returns the created deck's `slug` (or null on failure).
        createDeck: async ({ name = 'Untitled deck', templateSlug = null, templateId = null, columns = null, icon = null, makeActive = true } = {}) => {
          const session = _session;
          const state = get();
          if (state.decks.length >= state.limits.maxDecks) {
            if (typeof console !== 'undefined') console.warn(`[decks] limit reached (${state.limits.maxDecks})`);
            useToastStore.getState().actions.addToast(`You can have at most ${state.limits.maxDecks} decks. Delete a deck before creating another.`, 'error');
            return null;
          }
          try {
            const data = await decksApi.create({ name, templateSlug, templateId, columns, icon });
            if (session !== _session) return null;
            const deck = data?.deck;
            if (!deck?.slug) throw new Error('Missing created deck');
            set((s) => {
              const decks = s.decks.some(d => d.slug === deck.slug) ? s.decks : [...s.decks, deck];
              const next = makeActive ? deck.slug : s.activeDeckSlug;
              const activeDeck = decks.find(d => d.slug === next);
              return {
                decks,
                activeDeckSlug: next,
                ..._mirror(activeDeck, makeActive ? null : s.focusedColumnId),
              };
            });
            return deck.slug;
          } catch (err) {
            if (session !== _session) return null;
            const message = err?.status === 404 && (templateSlug || templateId)
              ? 'This recipe is no longer available. Please choose another recipe.'
              : err?.status === 400 && err?.message
                ? err.message
                : 'Could not create deck. Check your connection and try again.';
            useToastStore.getState().actions.addToast(message, 'error');
            if (typeof console !== 'undefined') console.warn('[decks] create failed', err?.message || err);
            return null;
          }
        },

        // Rename a deck (updates `name`, doesn't change `slug`).
        renameDeck: async (slug, name) => {
          const session = _session;
          const original = _findDeck(get(), slug)?.name;
          const trimmed = String(name || '').trim().slice(0, 120);
          if (!trimmed) return;
          // Optimistic local update
          set((state) => ({
            decks: state.decks.map(d => d.slug === slug ? { ...d, name: trimmed } : d),
          }));
          try {
            const data = await _enqueueWrite(slug, () => decksApi.update(slug, { name: trimmed }));
            if (session !== _session) return;
            const updated = data?.deck;
            if (updated) {
              set((state) => ({
                decks: state.decks.map(d => d.slug === slug && d.name === trimmed ? { ...d, name: updated.name } : d),
              }));
            }
          } catch (err) {
            if (session !== _session) return;
            set(state => ({
              decks: state.decks.map(d => d.slug === slug && d.name === trimmed ? { ...d, name: original } : d),
              syncError: 'Could not rename the deck. Please try again.',
            }));
            if (typeof console !== 'undefined') console.warn('[decks] rename failed', err?.message || err);
          }
        },

        // Soft-delete a deck. If the active deck is removed, falls back
        // to the next deck in the list (or null if none remain).
        deleteDeck: async (slug) => {
          const session = _session;
          try {
            await _enqueueWrite(slug, () => decksApi.delete(slug));
            if (session !== _session) return false;
          } catch {
            if (session === _session) set({ syncError: 'Could not delete the deck. Please try again.' });
            return false;
          }
          clearTimeout(_saveTimers[slug]);
          delete _saveTimers[slug];
          _dirtyDecks.delete(slug);
          let nextActive = null;
          set((state) => {
            const decks = state.decks.filter(d => d.slug !== slug);
            nextActive = state.activeDeckSlug === slug ? (decks[0]?.slug || null) : state.activeDeckSlug;
            const activeDeck = decks.find(d => d.slug === nextActive);
            return {
              decks,
              pendingColumnSlugs: [..._dirtyDecks.keys()],
              activeDeckSlug: nextActive,
              ..._mirror(activeDeck, null),
            };
          });
          return true;
        },

        // Reorder the deck tabs. `fromIndex` and `toIndex` are positions
        // in the current sorted decks array.
        reorderDecks: (fromIndex, toIndex) => {
          const session = _session;
          const before = get().decks;
          if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex) || fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= before.length || toIndex >= before.length) return;
          set((state) => {
            const decks = [...state.decks];
            if (fromIndex < 0 || fromIndex >= decks.length) return {};
            if (toIndex   < 0 || toIndex   >= decks.length) return {};
            const [moved] = decks.splice(fromIndex, 1);
            decks.splice(toIndex, 0, moved);
            return { decks };
          });
          // Persist the new order. Fire-and-forget; failures are logged
          // and reconciled by the next loadDecks().
          const ordered = get().decks;
          _enqueueWrite('tab-order', () => decksApi.reorder(ordered.map(d => d.slug))).catch(err => {
            if (session !== _session) return;
            set(state => ({ decks: state.decks === ordered ? before : state.decks, syncError: 'Could not save the deck order. Please try again.' }));
            if (typeof console !== 'undefined') console.warn('[decks] reorder failed', err?.message || err);
          });
        },

        // Wipe all client-side deck state (called on logout).
        clearDecks: () => {
          _session += 1;
          _writeChains.clear();
          _dirtyDecks.clear();
          Object.keys(_saveTimers).forEach(k => { clearTimeout(_saveTimers[k]); delete _saveTimers[k]; });
          set({
            decks: [],
            activeDeckSlug: null,
            isLoadingDecks: false,
            hasLoadedOnce: false,
            syncError: null,
            pendingColumnSlugs: [],
            limits: { maxDecks: MAX_DECKS_PER_USER, maxColumns: MAX_DECK_COLUMNS },
            columns: [],
            columnOrder: [],
            focusedColumnId: null,
          });
        },

        // ── Column actions (operate on the active deck) ────────────────

        // Add a column. Pass no args / `{}` for a blank chat column.
        // Pass `{ type: 'model'|'agent'|'tool'|'conversation', refId?, label? }`
        // for typed columns (refId optional until the user picks a resource).
        addColumn: (spec = {}) => {
          const state = get();
          const deck = _activeDeck(state);
          if (!deck) return null;
          if ((deck.columns || []).length >= state.limits.maxColumns) return null;

          let type = 'conversation';
          let refKind = 'Conversation';
          let refId = null;
          let label = '';
          let conversationId = null;

          if (spec && typeof spec === 'object') {
            if (spec.type && ['conversation', 'model', 'agent', 'tool'].includes(spec.type)) {
              type = spec.type;
              const map = { conversation: 'Conversation', model: 'Model', agent: 'Agent', tool: 'Tool' };
              refKind = map[type];
              refId = spec.refId || null;
              label = typeof spec.label === 'string' ? spec.label : '';
              if (type === 'conversation') conversationId = refId;
            }
          }

          const id = _newColId();
          const newColumn = {
            id,
            type,
            ref: { kind: refKind, id: refId },
            label,
            config: {},
            width: DEFAULT_DECK_COLUMN_WIDTH,
            conversationId,
          };
          const updatedDeck = { ...deck, columns: [...(deck.columns || []), newColumn] };

          set((s) => ({
            decks: s.decks.map(d => d.slug === deck.slug ? updatedDeck : d),
            ..._mirror(updatedDeck, s.focusedColumnId),
          }));
          _scheduleDeckSave(deck.slug, () => _findDeck(get(), deck.slug));
          return id;
        },

        removeColumn: (columnId, deckSlug = get().activeDeckSlug) => {
          const state = get();
          const deck = _findDeck(state, deckSlug);
          if (!deck) return;
          const updatedDeck = {
            ...deck,
            columns: (deck.columns || []).filter(c => c.id !== columnId),
          };
          set((s) => ({
            decks: s.decks.map(d => d.slug === deck.slug ? updatedDeck : d),
            ...(s.activeDeckSlug === deck.slug ? _mirror(updatedDeck, s.focusedColumnId === columnId ? null : s.focusedColumnId) : {}),
          }));
          _scheduleDeckSave(deck.slug, () => _findDeck(get(), deck.slug));
        },

        setColumnConversation: (columnId, conversationId, deckSlug = get().activeDeckSlug) => {
          const state = get();
          const deck = _findDeck(state, deckSlug);
          if (!deck) return;
          const updatedDeck = {
            ...deck,
            columns: (deck.columns || []).map(c =>
              c.id === columnId
                ? {
                    ...c,
                    type: 'conversation',
                    ref: { kind: 'Conversation', id: conversationId || null },
                    conversationId: conversationId || null,
                  }
                : c,
            ),
          };
          set((s) => ({
            decks: s.decks.map(d => d.slug === deck.slug ? updatedDeck : d),
            ...(s.activeDeckSlug === deck.slug ? _mirror(updatedDeck, s.focusedColumnId) : {}),
          }));
          _scheduleDeckSave(deck.slug, () => _findDeck(get(), deck.slug));
        },

        setColumnWidth: (columnId, width) => {
          if (!Number.isFinite(width)) return;
          const clamped = Math.max(MIN_DECK_COLUMN_WIDTH, Math.min(MAX_DECK_COLUMN_WIDTH, width));
          const state = get();
          const deck = _activeDeck(state);
          if (!deck) return;
          const updatedDeck = {
            ...deck,
            columns: (deck.columns || []).map(c =>
              c.id === columnId ? { ...c, width: clamped } : c,
            ),
          };
          set((s) => ({
            decks: s.decks.map(d => d.slug === deck.slug ? updatedDeck : d),
            ..._mirror(updatedDeck, s.focusedColumnId),
          }));
          _scheduleDeckSave(deck.slug, () => _findDeck(get(), deck.slug));
        },

        // Shallow-merge fields into one column (ref, label, config, type, etc.).
        updateColumn: (columnId, patch, deckSlug = get().activeDeckSlug) => {
          if (!patch || typeof patch !== 'object') return;
          const state = get();
          const deck = _findDeck(state, deckSlug);
          if (!deck) return;
          const updatedDeck = {
            ...deck,
            columns: (deck.columns || []).map(c =>
              c.id === columnId ? { ...c, ...patch } : c,
            ),
          };
          set((s) => ({
            decks: s.decks.map(d => d.slug === deck.slug ? updatedDeck : d),
            ...(s.activeDeckSlug === deck.slug ? _mirror(updatedDeck, s.focusedColumnId) : {}),
          }));
          _scheduleDeckSave(deck.slug, () => _findDeck(get(), deck.slug));
        },

        // Returns true the first time for this (column, kind, ref) tuple in-session.
        // Reorder columns within the active deck. Indices are positions
        // in the active deck's `columns` array.
        reorderColumns: (fromIndex, toIndex) => {
          if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex)) return;
          const state = get();
          const deck = _activeDeck(state);
          if (!deck) return;
          const cols = [...(deck.columns || [])];
          if (fromIndex < 0 || fromIndex >= cols.length) return;
          if (toIndex   < 0 || toIndex   >= cols.length) return;
          const [moved] = cols.splice(fromIndex, 1);
          cols.splice(toIndex, 0, moved);
          const updatedDeck = { ...deck, columns: cols };
          set((s) => ({
            decks: s.decks.map(d => d.slug === deck.slug ? updatedDeck : d),
            ..._mirror(updatedDeck, s.focusedColumnId),
          }));
          _scheduleDeckSave(deck.slug, () => _findDeck(get(), deck.slug));
        },

        moveColumnLeft: (columnId) => {
          const state = get();
          const deck = _activeDeck(state);
          if (!deck) return;
          const idx = (deck.columns || []).findIndex(c => c.id === columnId);
          if (idx > 0) get().actions.reorderColumns(idx, idx - 1);
        },

        moveColumnRight: (columnId) => {
          const state = get();
          const deck = _activeDeck(state);
          if (!deck) return;
          const cols = deck.columns || [];
          const idx = cols.findIndex(c => c.id === columnId);
          if (idx >= 0 && idx < cols.length - 1) get().actions.reorderColumns(idx, idx + 1);
        },

        equalizeColumnWidths: () => {
          const state = get();
          const deck = _activeDeck(state);
          if (!deck) return;
          const updatedDeck = {
            ...deck,
            columns: (deck.columns || []).map(c => ({ ...c, width: DEFAULT_DECK_COLUMN_WIDTH })),
          };
          set((s) => ({
            decks: s.decks.map(d => d.slug === deck.slug ? updatedDeck : d),
            ..._mirror(updatedDeck, s.focusedColumnId),
          }));
          _scheduleDeckSave(deck.slug, () => _findDeck(get(), deck.slug));
        },

        setFocusedColumnId: (columnId) => {
          set({ focusedColumnId: columnId });
        },

        // Wipe the active deck's columns. (Doesn't delete the deck.)
        clearDeck: () => {
          const state = get();
          const deck = _activeDeck(state);
          if (!deck) return;
          const updatedDeck = { ...deck, columns: [] };
          set((s) => ({
            decks: s.decks.map(d => d.slug === deck.slug ? updatedDeck : d),
            ..._mirror(updatedDeck, null),
          }));
          _scheduleDeckSave(deck.slug, () => _findDeck(get(), deck.slug));
        },
      },
    }),
    {
      name: 'deck-storage-v2',
      storage: createJSONStorage(() => getStorage()),
      merge: (persisted, current) => {
        const pending = (Array.isArray(persisted?.pendingColumnSlugs) ? persisted.pendingColumnSlugs : [])
          .filter(slug => persisted?.decks?.some(d => d.slug === slug));
        pending.forEach(slug => _dirtyDecks.set(slug, 1));
        return { ...current, ...persisted, pendingColumnSlugs: pending, syncError: pending.length ? UNSAVED_LAYOUT_MESSAGE : null };
      },
      partialize: (state) => ({
        decks: state.decks,
        pendingColumnSlugs: state.pendingColumnSlugs,
        activeDeckSlug: state.activeDeckSlug,
        // Mirrors are kept persistable so the page renders instantly on
        // reload, before the loadDecks() hydration round-trip completes.
        columns: state.columns,
        columnOrder: state.columnOrder,
      }),
    }
  )
);

export { useDeckStore };
export default useDeckStore;
